import type { V2App } from '@wix/ambassador-app-service-webapp/types';
import type {
  DocumentServicesObject,
  AppData,
  Callback,
} from '@wix/document-services-types';
import type { ActionPermissions } from '@wix/editor-platform-sdk-types';
import type { AppInstallOrigin } from '@wix/editor-platform-host-integration-apis';
import type { DocumentServicesAPI } from '@wix/editorx-core-api';
import { UseRequestResponse } from '../ui/appManager/hooks/useRequest';
import { ExperimentsBag } from '@wix/wix-experiments';

type OpenSettings = ({ path }: { path: string }) => void;

type GetAppPremiumUrlFunc = (
  appDefinitionId: string,
  origin?: string,
  referralAdditionalInfo?: string,
) => string;

export interface App extends V2App {
  id: string;
}

export interface AppMarketAddAppParams {
  appDefId: string;
  biData?: Object;
  appVersion?: string;
  settings?: any;
  platformOrigin?: AppInstallOrigin;
}

export interface AppManagerApiDependencies {
  isLiteMode?: boolean;
  isClassicEditor?: boolean;
  metaSiteInstance: string;
  queryWixApps?: () => Promise<App[] | undefined>; // TODO: remove this after removing usage in both editors
  addApp: (params: AppMarketAddAppParams) => Promise<void>;
  openAppMarket: (openMarketOrigin: string) => void;
  onUpdateApp: (appDefId: string, appVersion: string) => void;
  onUninstallApp: (
    appDefId: string,
    shouldShowConfirmationModal: boolean,
  ) => void;
  getAppDataByAppDefId: (
    appDefId: string,
  ) => ReturnType<
    | DocumentServicesAPI['platform']['getAppDataByAppDefId']
    | DocumentServicesObject['platform']['getAppDataByAppDefId']
  >;
  getAppDescriptor: (
    appDefId: string,
  ) => ReturnType<
    | DocumentServicesAPI['platform']['getAppDescriptor']
    | DocumentServicesObject['platform']['getAppDescriptor']
  >;
  getInstalledEditorApps: () => ReturnType<
    | DocumentServicesAPI['platform']['getInstalledEditorApps']
    | DocumentServicesObject['platform']['getInstalledEditorApps']
  >;
  getInstalledAppsOnOtherBranches?: () => string[];
  isAppUpdateAvailable: (appDefId: string, latestVersion?: string) => boolean;
  openSettings: OpenSettings;
  openHelpCenter: (helpId: string) => void;
  openBusinessManager: (appDefId: string, url: string) => Promise<void>;
  openPagesPanel: (appDefId: string) => void;
  openAddPanel: (appDefId: string) => void;
  openDashboardPanel: (appDefId: string) => void;
  notifyApplication: (appDefId: string, actionId: string) => void;
  addGrantedToPermissions: (
    permissions?: ActionPermissions['permissions'],
  ) => ActionPermissions['permissions'];
  isPremium: () => boolean;
  getSitePremiumUrl: (appDefId: string, origin?: string) => string;
  getAppPremiumUrl: GetAppPremiumUrlFunc;
  appHasPremiumOffering?: (applicationId: number) => boolean;
  appHasPremiumPlan?: (appDefId: string) => boolean;
  appHasPages: (appDefId: string) => Promise<boolean>;
  appHasWidgets: (appDefId: string) => Promise<boolean>;
  appHasDashboard: (appDefId: string) => boolean;
  checkAppsCompletion: (callback: Callback) => boolean;
}

export interface AppManagerStateProps {
  locale: string;
  appsList: string[];
  loadedApps?: UseRequestResponse<App[] | undefined>; // TODO: remove this after removing usage in both editors
  experiments?: ExperimentsBag;
  userPermissions: Set<string>;
  pendingAppDefIds: Record<string, boolean>;
  loadedManifestsCount?: number;
  allAvailableToUpdateApps: AppData[];
}

export interface AppManagerDispatchProps {
  apiDependencies: AppManagerApiDependencies;
}

export interface AppManagerClassicPanelProps {
  panelName?: string;
  panelIndex?: number;
  selectedAppId?: string;
  selectedAppFromUrl?: string;
}

export enum MyBusinessOrigin {
  Sidebar = 'SIDEBAR',
  Dashboard = 'DASHBOARD',
  AppInstallation = 'APP_INSTALLATION',
  Deeplink = 'DEEPLINK',
}

export interface AppManagerOwnProps extends AppManagerClassicPanelProps {
  onPanelTitleReady?(title: string): void;
  origin?: MyBusinessOrigin;
}

export type AppManagerProps = AppManagerOwnProps &
  AppManagerStateProps &
  AppManagerDispatchProps;
